import { useMemo } from "react";
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { BsPencilSquare } from "react-icons/bs";
import "./BudgetSection.css";
import { Link } from "react-router-dom";
import { useAppContext } from "../../../lib/contextLib";
import { calculateTotals } from "../../../lib/calculateTotals";
import { toNormalizedFrequency } from "../../../lib/frequencyMap";
import { useBudgetItems } from '../../../lib/hooks/useBudgetQueries';
import ItemListGroups from "./ItemListGroups";

type ItemType = 'income' | 'expense' | 'savings';

interface BudgetSectionProps {
  itemType: ItemType;
  accentColor: string;
  addLabel: string;
  addPath: string;
  emptyMessage: string;
}

interface BucketItem {
  itemId: string;
  itemType: string;
  itemSubType: string;
  description: string;
  amount: string | number;
  frequency: string;
}

interface TotalRow {
  index: number;
  typeTotal: string;
  amountYear: number;
  amountQuarter: number;
  amountMonth: number;
  amountFortnight: number;
  amountWeek: number;
}

export default function BudgetSection({ itemType, accentColor, addLabel, addPath, emptyMessage }: BudgetSectionProps) {
  const { isAuthenticated } = useAppContext();
  const { data: budgetItems = [], isLoading } = useBudgetItems(isAuthenticated, { types: [itemType] });

  const { totals, yearBudgetItems, quarterBudgetItems, monthBudgetItems, fortnightBudgetItems, weekBudgetItems } = useMemo(() => {
    let totalYear = 0;
    const year: BucketItem[] = [], quarter: BucketItem[] = [], month: BucketItem[] = [], fortnight: BucketItem[] = [], week: BucketItem[] = [];
    for (const b of budgetItems) {
      totalYear += typeof b.yearlyAmountCents === 'number'
        ? b.yearlyAmountCents / 100
        : calculateTotals(b.amount, b.frequency, 'YEAR');
      const nf = toNormalizedFrequency(b.frequency);
      const base: BucketItem = { itemId: b.itemId, itemType: b.itemType, itemSubType: b.itemSubType, description: b.description, amount: b.amount, frequency: nf };
      if (nf === 'YEAR')      year.push(base);
      if (nf === 'QUARTER')   quarter.push(base);
      if (nf === 'MONTH')     month.push(base);
      if (nf === 'FORTNIGHT') fortnight.push(base);
      if (nf === 'WEEK')      week.push(base);
    }
    const breakdown = (y: number) => ({ amountYear: y, amountQuarter: y / 4, amountMonth: y / 12, amountFortnight: y / 26, amountWeek: y / 52 });
    const totals: TotalRow[] = budgetItems.length ? [{ index: 0, typeTotal: itemType, ...breakdown(totalYear) }] : [];
    return { totals, yearBudgetItems: year, quarterBudgetItems: quarter, monthBudgetItems: month, fortnightBudgetItems: fortnight, weekBudgetItems: week };
  }, [budgetItems, itemType]);

  const summaryRows = useMemo(() => {
    if (!totals.length) return [];
    const t = totals[0];
    return [
      { label: 'Weekly', value: t.amountWeek },
      { label: 'Fortnightly', value: t.amountFortnight },
      { label: 'Monthly', value: t.amountMonth },
      { label: 'Quarterly', value: t.amountQuarter },
      { label: 'Yearly', value: t.amountYear },
    ];
  }, [totals]);

  const fmt = (v: number) => v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

  if (!isAuthenticated) {
    return (
      <div className="lander">
        <h1>Making Budgets</h1>
        <p className="text-muted">A simple tool for making budgets</p>
      </div>
    );
  }

  return (
    <div className="container pt-1 pb-4">
      <div className="stack-lg">
        {summaryRows.length ? (
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'var(--color-border)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'var(--color-text-muted)', borderColor: 'var(--color-border)', fontSize: '.8rem', letterSpacing: '.5px', textTransform: 'uppercase' } }}>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map((row) => (
                  <TableRow key={row.label} sx={{ '& td': { borderColor: 'var(--color-border)', color: 'var(--color-text)', py: 1.1 }, '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: `${accentColor} !important`, fontSize: '.95rem' }}>{fmt(row.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : isLoading ? (
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'var(--color-border)' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'var(--color-text-muted)', borderColor: 'var(--color-border)', fontSize: '.8rem', letterSpacing: '.5px', textTransform: 'uppercase' } }}>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {['Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Yearly'].map(label => (
                  <TableRow key={label} sx={{ '& td': { borderColor: 'var(--color-border)', py: 1.1 }, '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                    <TableCell align="right"><Skeleton variant="text" width={80} height={24} sx={{ ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper variant="outlined" sx={{ minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', borderColor: 'var(--color-border)' }}>
            <span className="text-muted-strong">{emptyMessage}</span>
          </Paper>
        )}
        <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, '& .MuiButton-root': { fontWeight: 600, letterSpacing: '.5px' } }}>
          <Stack direction="row" justifyContent="center">
            <Button
              size="small"
              variant="outlined"
              component={Link}
              to={addPath}
              startIcon={<BsPencilSquare size={14} />}
              sx={{ color: accentColor, borderColor: accentColor, '&:hover': { borderColor: accentColor, bgcolor: `${accentColor}18` } }}
            >
              {addLabel}
            </Button>
          </Stack>
          <ItemListGroups
            itemType={itemType}
            accentColor={accentColor}
            isLoading={isLoading}
            yearBudgetItems={yearBudgetItems}
            quarterBudgetItems={quarterBudgetItems}
            monthBudgetItems={monthBudgetItems}
            fortnightBudgetItems={fortnightBudgetItems}
            weekBudgetItems={weekBudgetItems}
          />
        </Paper>
      </div>
    </div>
  );
}
